/**
 * Tła ról OVL + warstwa strukturalna (rodzina chrome).
 * Kolor = drugorzędny; kształt / typografia / cień / ruch = rodzina.
 *
 * Semafor (osobno): zieleń = wybór; pomarańcz = uwaga; czerwień = problem.
 * Wynik = indigo (nie zieleń).
 */

export type OpSurfaceTone =
  | "work"
  | "params"
  | "log"
  | "process"
  | "result"
  | "help"
  | "optional"
  | "now"
  | "danger"
  | "review"
  | "preview";

export type OpSurfaceFamily =
  | "focus"
  | "work"
  | "io"
  | "live"
  | "signal"
  | "ambient"
  | "machine";

export type OpChromeKind =
  | "elevated"
  | "flat"
  | "topRail"
  | "leftRail"
  | "dashed"
  | "darkMono"
  | "live"
  | "signalRail";

export type OpSurfaceMeta = {
  box: string;
  label: string;
  hint: string;
  swatch: string;
  ink?: string;
  family: OpSurfaceFamily;
  chrome: OpChromeKind;
};

export const OP_SURFACE: Record<OpSurfaceTone, OpSurfaceMeta> = {
  work: {
    family: "work",
    chrome: "flat",
    // Płótno: cienka ramka, miękkie rogi, zero pastelu / cienia
    box: "rounded-md border border-slate-300 bg-white shadow-none",
    label: "Praca",
    hint: "Główna interakcja (krok, CTA, pola)",
    swatch: "bg-white border border-slate-300 rounded-md",
  },
  params: {
    family: "io",
    chrome: "topRail",
    // Wejście: pasek GÓRNY (nie lewy) = inny wektor niż wynik
    box: "rounded-md border-2 border-blue-600 border-t-[6px] border-t-blue-700 bg-blue-100",
    label: "Parametry",
    hint: "Wejście, meta, typ, styl, ustawienia",
    swatch: "bg-blue-100 border-2 border-t-4 border-t-blue-700 border-blue-600",
    ink: "text-blue-950",
  },
  log: {
    family: "machine",
    chrome: "darkMono",
    // Terminal: ostre rogi, mono, inset
    box: [
      "rounded-none border-2 border-zinc-950 bg-zinc-900 font-mono text-xs text-zinc-100 shadow-inner",
      "[&_h1]:text-zinc-50 [&_h2]:text-zinc-50 [&_h3]:text-zinc-50 [&_h4]:text-zinc-50",
      "[&_p]:text-zinc-200 [&_li]:text-zinc-200 [&_dt]:text-zinc-400 [&_dd]:text-zinc-100",
      "[&_summary]:text-zinc-100 [&_label]:text-zinc-200",
      "[&_.text-slate-800]:!text-zinc-100 [&_.text-slate-700]:!text-zinc-200 [&_.text-slate-900]:!text-zinc-50",
      "[&_.text-zinc-900]:!text-zinc-50 [&_.text-muted-foreground]:!text-zinc-400",
    ].join(" "),
    label: "Log",
    hint: "Debug / eventy / mono (ciemny terminal)",
    swatch: "bg-zinc-900 border-2 border-zinc-950 rounded-none",
    ink: "text-zinc-100",
  },
  process: {
    family: "live",
    chrome: "live",
    // Live: cyan (nie brand/róż – to wygląda jak błąd). Duże zaokrąglenie, pierścień.
    box: "rounded-2xl border-2 border-cyan-600 bg-cyan-100 shadow-lg ring-2 ring-cyan-400/35",
    label: "Proces",
    hint: "Job w toku, pasek etapów (nie semafor błędu)",
    swatch: "bg-cyan-100 border-2 border-cyan-600 rounded-2xl",
    ink: "text-cyan-950",
  },
  result: {
    family: "io",
    chrome: "leftRail",
    // Wyjście: pasek LEWY 8px + miękkie karty
    box: "rounded-2xl border-2 border-l-8 border-indigo-700 border-l-indigo-700 bg-indigo-50 shadow-sm",
    label: "Wynik",
    hint: "Karty, output (zieleń = dopiero Twój wybór)",
    swatch: "bg-indigo-50 border-2 border-l-8 border-l-indigo-700 border-indigo-700 rounded-2xl",
    ink: "text-indigo-950",
  },
  help: {
    family: "ambient",
    chrome: "dashed",
    // Tip: dashed, miękkie, płaskie, mniejszy „ciężar”
    box: "rounded-2xl border border-dashed border-yellow-600 bg-yellow-50/90 shadow-none",
    label: "Pomoc",
    hint: "Tip, legenda (żółty; nie mylić z parametrami ani uwagą)",
    swatch: "bg-yellow-50 border border-dashed border-yellow-600 rounded-2xl",
    ink: "text-yellow-950",
  },
  optional: {
    family: "ambient",
    chrome: "dashed",
    // Fold: mocno dashed + hatch + bardzo miękkie rogi
    box: [
      "rounded-3xl border-2 border-dashed border-violet-400 bg-violet-50/70 shadow-none",
      "bg-[repeating-linear-gradient(135deg,transparent,transparent_7px,rgba(139,92,246,0.07)_7px,rgba(139,92,246,0.07)_14px)]",
    ].join(" "),
    label: "Opcjonalne",
    hint: "Fold, nie blokuje ścieżki",
    swatch: "bg-violet-50 border-2 border-dashed border-violet-400 rounded-3xl",
    ink: "text-violet-950",
  },
  now: {
    family: "focus",
    chrome: "elevated",
    // Fokus: ostrejsze rogi, pasek 8px, najmocniejszy cień + ring
    box: "rounded-sm border-2 border-l-8 border-brand border-l-brand bg-brand/25 shadow-lg ring-1 ring-brand/25",
    label: "Teraz",
    hint: "Sticky fokus operatora",
    swatch: "bg-brand/25 border-2 border-l-8 border-l-brand border-brand rounded-sm",
    ink: "text-slate-900",
  },
  danger: {
    family: "signal",
    chrome: "signalRail",
    // Sygnał: ostre rogi, prawie białe, pasek 8px
    box: "rounded-none border border-red-200 border-l-8 border-l-red-600 bg-white shadow-none",
    label: "Błąd",
    hint: "Gate, timeout, reklamacja",
    swatch: "bg-white border border-l-8 border-l-red-600 border-red-200 rounded-none",
    ink: "text-red-950",
  },
  review: {
    family: "signal",
    chrome: "signalRail",
    box: "rounded-none border border-orange-200 border-l-8 border-l-orange-500 bg-white shadow-none",
    label: "Recenzja",
    hint: "Werdykt AI, checklist wdrożeń",
    swatch: "bg-white border border-l-8 border-l-orange-500 border-orange-200 rounded-none",
    ink: "text-orange-950",
  },
  preview: {
    family: "machine",
    chrome: "darkMono",
    box: "rounded-sm border-2 border-slate-700 bg-slate-950 font-mono text-slate-100 shadow-inner",
    label: "Podgląd",
    hint: "Canvas PNG / skład slajdu",
    swatch: "bg-slate-950 border-2 border-slate-700 rounded-sm",
    ink: "text-slate-100",
  },
};

export const OP_SURFACE_FAMILIES: {
  id: OpSurfaceFamily;
  title: string;
  blurb: string;
  tones: OpSurfaceTone[];
}[] = [
  {
    id: "focus",
    title: "Focus",
    blurb: "Ostre rogi, pasek 8px brand, shadow-lg + ring. Sticky. Najpierw tu.",
    tones: ["now"],
  },
  {
    id: "work",
    title: "Work",
    blurb: "Białe płótno, cienka ramka, zero cienia. Tu klikasz.",
    tones: ["work"],
  },
  {
    id: "io",
    title: "Wejście · wyjście",
    blurb: "Params = pasek GÓRNY niebieski. Wynik = pasek LEWY indigo + rounded-2xl.",
    tones: ["params", "result"],
  },
  {
    id: "live",
    title: "Live",
    blurb: "Rounded-2xl, shadow-lg, ring cyan, pulse/spinner. Job żyje (nie brand/róż).",
    tones: ["process"],
  },
  {
    id: "signal",
    title: "Sygnał",
    blurb: "Ostre rogi (rounded-none), białe tło, pasek 8px semafora. Alarm, nie pastel.",
    tones: ["review", "danger"],
  },
  {
    id: "ambient",
    title: "Ambient",
    blurb: "Dashed + miękkie. Help żółty tip. Optional: hatch + rounded-3xl.",
    tones: ["help", "optional"],
  },
  {
    id: "machine",
    title: "Maszyna",
    blurb: "Ciemne, mono, ostre/inset. Log = terminal. Preview = canvas chrome.",
    tones: ["log", "preview"],
  },
];

export const OP_RESULT_CARD = {
  idle: [
    "relative overflow-hidden rounded-2xl border-2 border-indigo-700 bg-white text-left shadow-sm",
    "pl-5 before:absolute before:inset-y-0 before:left-0 before:w-2 before:bg-indigo-600",
    "hover:border-indigo-800 hover:bg-indigo-50/80",
  ].join(" "),
  selected: [
    "relative overflow-hidden rounded-2xl border-4 border-success bg-emerald-50 text-left shadow-lg ring-2 ring-success/40",
    "pl-5 before:absolute before:inset-y-0 before:left-0 before:w-2 before:bg-success",
  ].join(" "),
  static: [
    "relative overflow-hidden rounded-xl border-2 border-indigo-700 bg-white shadow-sm",
    "pl-5 before:absolute before:inset-y-0 before:left-0 before:w-2 before:bg-indigo-600",
  ].join(" "),
};
